<script setup lang="ts">
import { computed } from 'vue';
import ArrowLeft from '~icons/carbon/chevron-left';
import ArrowRight from '~icons/carbon/chevron-right';

const props = defineProps<{
  currentPage: number;
  totalPages: number;
  totalCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  setPage: (pageNumber: number) => void;
}>();

const reversedPageNumbers = computed(() => {
  const maxPages = Math.min(props.currentPage - 1, 2);
  return Array.from({ length: maxPages }, (_, index) => maxPages - index);
});

function nextPage() {
  props.hasNextPage && props.setPage(props.currentPage + 1);
}

function previousPage() {
  props.hasPreviousPage && props.setPage(props.currentPage - 1);
}
</script>

<template>
  <nav class="z-0 flex -space-x-px shadow-sm">
    <!-- Previous Page Button -->
    <button :disabled="!hasPreviousPage" class="page-btn rounded-l-md" @click="previousPage()">
      <ArrowLeft class="-mx-1" />
    </button>

    <!-- First Page Button -->
    <button v-if="currentPage > 3" class="page-btn" @click="setPage(1)">1</button>

    <!-- Dots for indicating previous pages -->
    <span v-if="currentPage > 4" class="page-btn --ellipsis">...</span>

    <!-- Previous 3 Pages -->
    <button
      v-for="pageNumber in reversedPageNumbers"
      :key="pageNumber"
      class="page-btn"
      @click="setPage(currentPage - pageNumber)"
    >
      {{ currentPage - pageNumber }}
    </button>

    <!-- Current Page -->
    <button class="page-btn --selected">{{ currentPage }}</button>

    <!-- Next 3 Pages -->
    <button
      v-for="pageNumber in Math.min(totalPages - currentPage, 2)"
      :key="pageNumber"
      class="page-btn"
      @click="setPage(currentPage + pageNumber)"
    >
      {{ currentPage + pageNumber }}
    </button>

    <!-- Dots for indicating next pages -->
    <span v-if="currentPage < totalPages - 3" class="page-btn --ellipsis">...</span>

    <!-- Last Page Button -->
    <button v-if="currentPage < totalPages - 2" class="page-btn" @click="setPage(totalPages)">
      {{ totalPages }}
    </button>

    <!-- Next Page Button -->
    <button :disabled="!hasNextPage" class="page-btn rounded-r-md" @click="nextPage()">
      <ArrowRight class="-mx-1" />
    </button>
  </nav>
</template>

<style scoped lang="postcss">
.page-btn {
  @apply relative inline-flex items-center border bg-white px-3.5 py-2 text-xs hover:bg-new-gray-50 dark:bg-gray-675;
  @apply outline-none focus-visible:outline-offset-0 focus-visible:ring-1 focus-visible:ring-inset;
  @apply disabled:bg-new-gray-50 disabled:text-new-gray-600 dark:border-new-gray-950/80 disabled:dark:bg-new-gray-900;

  &.--selected {
    @apply cursor-default bg-new-green-300 dark:bg-new-green-700 dark:text-new-content-900;
  }

  &.--number {
    @apply px-3.5;
  }

  &.--ellipsis {
    @apply cursor-default hover:bg-white;
  }
}
</style>
